import React, { useContext } from 'react';
import { Card, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Fade from 'react-reveal/Fade';
import { ThemeContext } from 'styled-components';
import { useHistory } from 'react-router-dom';
import AppContext from '../../AppContext';
import { getTranslation } from '../../constants/translations';
import '../../css/services.css';

const styles = {
  badgeStyle: {
    padding: '5px 10px',
    margin: 5,
  },
  cardStyle: {
    borderRadius: 10,
  },
  cardTitleStyle: {
    fontSize: 24,
    fontWeight: 700,
    textAlign: 'center', // Center the title
  },
  cardTextStyle: {
    textAlign: 'left', // Align description to the left
  },
};

const ServiceCard = ({ service }) => {
  const theme = useContext(ThemeContext);
  const { language } = useContext(AppContext);
  const t = getTranslation(language);
  const history = useHistory();

  if (!service) return null; // Avoid rendering if `service` is undefined

  const localizedService =
    service.translationKey && t.services?.[service.translationKey]
      ? {
          ...service,
          title: t.services[service.translationKey].title || service.title,
          description: t.services[service.translationKey].description || service.description,
          ctaLabel: t.services[service.translationKey].ctaLabel || service.ctaLabel,
        }
      : service;

  const isDark = theme.bsPrimaryVariant === 'dark';
  const cardPalette = isDark
    ? {
        '--service-card-surface': 'linear-gradient(180deg, rgba(16, 24, 33, 0.96) 0%, rgba(24, 36, 48, 0.94) 100%)',
        '--service-card-border': 'rgba(125, 211, 192, 0.18)',
        '--service-card-shadow': '0 22px 48px rgba(2, 6, 23, 0.34)',
        '--service-card-image-surface': 'linear-gradient(180deg, rgba(233, 239, 244, 0.08) 0%, rgba(148, 163, 184, 0.14) 100%)',
      }
    : {
        '--service-card-surface': 'linear-gradient(180deg, rgba(255, 250, 242, 0.97) 0%, rgba(241, 236, 227, 0.95) 100%)',
        '--service-card-border': 'rgba(182, 148, 88, 0.2)',
        '--service-card-shadow': '0 20px 44px rgba(94, 78, 52, 0.12)',
        '--service-card-image-surface': 'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 238, 227, 0.96) 100%)',
      };

  return (
    <Fade bottom>
      <Col>
        <Card
          className="service-card"
          style={{
            ...styles.cardStyle,
            ...cardPalette,
            background: 'var(--service-card-surface)',
            borderColor: 'var(--service-card-border)',
            boxShadow: 'var(--service-card-shadow)',
          }}
          text={theme.bsSecondaryVariant}
          role={localizedService.ctaHref ? 'link' : undefined}
          tabIndex={localizedService.ctaHref ? 0 : undefined}
          onClick={() => {
            if (localizedService.ctaHref) {
              history.push(localizedService.ctaHref);
            }
          }}
          onKeyDown={(event) => {
            if (localizedService.ctaHref && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              history.push(localizedService.ctaHref);
            }
          }}
        >
          {/* Display image if available */}
          {localizedService.image && (
            <Card.Img
              className="service-card-image"
              variant="top"
              src={localizedService.image}
              style={localizedService.customImageStyle || { width: '100%', height: 'auto' }}
            />
          )}

          {/* Card Body */}
          <Card.Body className="service-card-body">
            <Card.Title className="service-card-title" style={styles.cardTitleStyle}>{localizedService.title}</Card.Title>
            <Card.Text
              className="service-card-text"
              style={{ ...styles.cardTextStyle, color: theme.color }}
            >
              {localizedService.description}
            </Card.Text>
            {localizedService.ctaLabel && localizedService.ctaHref && (
              <span
                className="service-card-cta"
                style={{
                  display: 'inline-block',
                  marginTop: 12,
                  padding: '10px 16px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 700,
                  backgroundColor: theme.accentColor,
                  color: '#ffffff',
                }}
              >
                {localizedService.ctaLabel}
              </span>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Fade>
  );
};

ServiceCard.propTypes = {
  service: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string,
    ctaHref: PropTypes.string,
    ctaLabel: PropTypes.string,
    translationKey: PropTypes.string,
    customImageStyle: PropTypes.object,
  }).isRequired,
};

export default ServiceCard;
